import { MakeLogicType, actions, connect, kea, listeners, path, props, reducers, selectors } from 'kea'

import { createFuse, Fuse } from 'lib/utils/fuseSearch'
import { membersLogic } from 'scenes/organization/membersLogic'
import { rolesLogic } from 'scenes/settings/organization/Permissions/Roles/rolesLogic'

import { ErrorTrackingIssue } from '~/queries/schema/schema-general'
import type { OrganizationMemberType, RoleType } from '~/types'

export type ErrorTrackingAssigneeSelectProps = {
    assignee: ErrorTrackingIssue['assignee']
}

export type UserAssignee = {
    id: number
    type: 'user'
    user: OrganizationMemberType['user']
}

export type RoleAssignee = {
    id: string
    type: 'role'
    role: RoleType
}

export type Assignee = UserAssignee | RoleAssignee | null

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface assigneeSelectLogicValues {
    filteredMembers: OrganizationMemberType[] // membersLogic
    me: OrganizationMemberType | null // membersLogic
    meFirstMembers: OrganizationMemberType[] // membersLogic
    membersLoading: boolean // membersLogic
    roles: RoleType[] // rolesLogic
    rolesLoading: boolean // rolesLogic
    filteredRoles: RoleType[]
    loading: boolean
    myRoles: RoleType[]
    resolveAssignee: (assignee: ErrorTrackingIssue['assignee']) => Assignee
    rolesFuse: Fuse<RoleType>
    search: string
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface assigneeSelectLogicActions {
    ensureAllMembersLoaded: () => {
        value: true
    } // membersLogic
    setMembersSearch: (search: any) => {
        search: any
    } // membersLogic
    ensureAssigneeTypesLoaded: () => {
        value: true
    }
    setSearch: (search: any) => {
        search: any
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface assigneeSelectLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        loading: (membersLoading: boolean, rolesLoading: boolean) => boolean
        rolesFuse: (roles: RoleType[]) => Fuse<RoleType>
        filteredRoles: (roles: RoleType[], rolesFuse: Fuse<RoleType>, search: string) => RoleType[]
        myRoles: (filteredRoles: RoleType[], me: OrganizationMemberType | null) => RoleType[]
        resolveAssignee: (
            roles: RoleType[],
            meFirstMembers: OrganizationMemberType[]
        ) => (assignee: ErrorTrackingIssue['assignee']) => Assignee
    }
}

export type assigneeSelectLogicType = MakeLogicType<
    assigneeSelectLogicValues,
    assigneeSelectLogicActions,
    ErrorTrackingAssigneeSelectProps,
    assigneeSelectLogicMeta
>

export const assigneeSelectLogic = kea<assigneeSelectLogicType>([
    path(['products', 'error_tracking', 'components', 'Assignee', 'assigneeSelectLogic']),
    props({} as ErrorTrackingAssigneeSelectProps),

    connect(() => ({
        values: [
            membersLogic,
            ['me', 'meFirstMembers', 'filteredMembers', 'membersLoading'],
            rolesLogic,
            ['roles', 'rolesLoading'],
        ],
        actions: [membersLogic, ['setSearch as setMembersSearch', 'ensureAllMembersLoaded']],
    })),

    actions({
        ensureAssigneeTypesLoaded: true,
        setSearch: (search) => ({ search }),
    }),

    reducers({
        search: ['', { setSearch: (_, { search }) => search }],
    }),

    listeners(({ values, actions }) => ({
        setSearch: () => {
            actions.setMembersSearch(values.search)
        },
        ensureAssigneeTypesLoaded: () => {
            actions.ensureAllMembersLoaded()
        },
    })),

    selectors({
        loading: [
            (s) => [s.membersLoading, s.rolesLoading],
            (membersLoading: boolean, rolesLoading: boolean): boolean => membersLoading || rolesLoading,
        ],

        rolesFuse: [
            (s) => [s.roles],
            (roles: RoleType[]): Fuse<RoleType> => createFuse(roles, { keys: ['name'], threshold: 0.3 }),
        ],
        filteredRoles: [
            (s) => [s.roles, s.rolesFuse, s.search],
            (roles: RoleType[], rolesFuse: Fuse<RoleType>, search: string): RoleType[] =>
                search ? rolesFuse.search(search).map((result) => result.item) : (roles ?? []),
        ],
        myRoles: [
            (s) => [s.filteredRoles, s.me],
            (roles: RoleType[], me: OrganizationMemberType | null): RoleType[] =>
                me
                    ? roles.filter((role) => (role.members ?? []).some((member) => member.user.uuid === me.user.uuid))
                    : [],
        ],

        resolveAssignee: [
            (s) => [s.roles, s.meFirstMembers],
            (
                roles: RoleType[],
                members: OrganizationMemberType[]
            ): ((assignee: ErrorTrackingIssue['assignee']) => Assignee) => {
                return (assignee: ErrorTrackingIssue['assignee']) => {
                    if (assignee) {
                        if (assignee.type === 'role') {
                            const assignedRole = roles.find((role) => role.id === assignee.id)
                            return assignedRole
                                ? {
                                      id: assignedRole.id,
                                      type: 'role',
                                      role: assignedRole,
                                  }
                                : null
                        }

                        const assignedMember = members.find((member) => member.user.id === assignee.id)
                        return assignedMember
                            ? {
                                  id: assignedMember.user.id,
                                  type: 'user',
                                  user: assignedMember.user,
                              }
                            : null
                    }

                    return null
                }
            },
        ],
    }),
])
